// Live Feed tape — the game-state lanes.
//
// Game state reaches g1 outside the market feed, over two lanes: the legacy
// /api/analytics/game-states poll (gameStatesStore, VITE_HYDRA_ANALYTICS_URL)
// and the Core game-state lane (coreGameStates, VITE_HYDRA_GAMESTATE_URL).
// The tape's "Odds and Game State" column shows both. These helpers turn each
// lane's store into tape rows and name the engine behind it.
import type { UnabatedGameState } from '@/services/hydraAnalytics';
import type { AdmittedGameState } from './admission';
import { parseGameKeyV2 } from '@/services/analyticsGameKey';

export type GameStateTapeLane = 'poll' | 'core';

/** What the tape shows of one game's state. */
export interface GameStateTapeState {
  key: string;
  sport: string;
  statusId: number;
  awayScore: number | null;
  homeScore: number | null;
  periodLabel: string | null;
  gameClock: string | null;
  /** Selected provider on the Core lane (`unabated`, `mlb-statsapi` …). The
   *  poll lane is Unabated-only, so it leaves this unset. */
  provider?: string;
  /** `AWAY@HOME` when the record names its sides. The game key's team pair is
   *  alpha-sorted, so it cannot orient the scores on its own. */
  matchup?: string;
}

/** A game is logged only when this changes. The poll returns every requested
 *  game every 5 s; logging each response would bury the one game whose score
 *  actually moved under rows that say nothing. */
export function gameStateTapeSignature(s: GameStateTapeState): string {
  return [s.statusId, s.awayScore, s.homeScore, s.periodLabel, s.gameClock, s.provider].join('\u0000');
}

function keySport(key: string): string {
  return parseGameKeyV2(key)?.sport ?? '?';
}

/** Poll lane → tape states. A producer null (game not known) is not a state. */
export function pollTapeStates(byGameKey: ReadonlyMap<string, UnabatedGameState | null>): GameStateTapeState[] {
  const out: GameStateTapeState[] = [];
  for (const [key, v] of byGameKey) {
    if (!v) continue;
    out.push({
      key,
      sport: keySport(key),
      statusId: v.statusId,
      awayScore: v.awayScore ?? null,
      homeScore: v.homeScore ?? null,
      periodLabel: v.periodLabel ?? null,
      gameClock: v.gameClock ?? null,
      matchup: v.consensus ? `${v.consensus.awayTeam}@${v.consensus.homeTeam}` : undefined,
    });
  }
  return out;
}

/** Core lane → tape states. A withdrawn record (state null) is skipped. */
export function coreTapeStates(records: ReadonlyMap<string, AdmittedGameState>): GameStateTapeState[] {
  const out: GameStateTapeState[] = [];
  for (const [key, rec] of records) {
    const s = rec.state;
    if (!s) continue;
    out.push({
      key,
      sport: s.sport ? s.sport.toLowerCase() : keySport(key),
      statusId: s.statusId,
      awayScore: s.awayScore,
      homeScore: s.homeScore,
      periodLabel: s.periodLabel ?? null,
      gameClock: s.gameClock,
      provider: s.source,
      matchup: `${s.awayTeam}@${s.homeTeam}`,
    });
  }
  return out;
}

export function hostOf(url: string | undefined): string | null {
  if (!url) return null;
  try {
    return new URL(url).hostname.toLowerCase() || null;
  } catch {
    return null;
  }
}

/** The market-feed engine that shares a game-state lane's host. The lane's
 *  REST/WS base and the engine's WS URL differ in scheme, port and path, never
 *  in host (`https://hc1.qeezi.com` ↔ `wss://hc1.qeezi.com/ws`). */
export function feedSharingHost<T extends { url?: string }>(
  url: string | undefined,
  feeds: readonly T[],
): T | undefined {
  const host = hostOf(url);
  return host == null ? undefined : feeds.find((f) => hostOf(f.url) === host);
}

/** Fallback name for a lane whose host no market feed shares: the host's first
 *  DNS label (`sh0-hc.qeezi.com` → `sh0-hc`). That is the instance's real
 *  name, never a guessed fleet identity — the URL is on the tooltip. */
export function hostLabel(url: string | undefined): string | undefined {
  return hostOf(url)?.split('.')[0] || undefined;
}
